import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Lock, ShieldAlert, CheckCircle2, ChevronRight, X, BookOpen } from 'lucide-react'
import { accountingApi, type Account } from '@/modules/accounting/api/accounting.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CLASS_LABELS: Record<number, string> = {
  1: 'Classe 1 — Ressources durables',
  2: 'Classe 2 — Emplois stables',
  3: 'Classe 3 — Stocks',
  4: 'Classe 4 — Tiers',
  5: 'Classe 5 — Trésorerie',
  6: 'Classe 6 — Charges',
  7: 'Classe 7 — Produits',
  8: 'Classe 8 — Autres charges et produits',
  9: 'Classe 9 — Comptes analytiques',
}

const CLASS_COLORS: Record<number, string> = {
  1: 'bg-[var(--mauve-bg)] text-[var(--mauve)]',
  2: 'bg-[var(--blue-bg)] text-[var(--blue-d)]',
  3: 'bg-[#ECFEFF] text-[#0E7490]',
  4: 'bg-[var(--amber-bg)] text-[var(--amber-text)]',
  5: 'bg-[var(--green)]/10 text-[var(--green-text)]',
  6: 'bg-[var(--red-bg)] text-[var(--red-text)]',
  7: 'bg-[var(--green-bg)] text-[var(--green-text)]',
  8: 'bg-[var(--bd2)]/10 text-[var(--tx3)]',
  9: 'bg-[#FDF2F8] text-[#DB2777]',
}

// ─── Modal Création ───────────────────────────────────────────────────────────

function CreateAccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc       = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [form, setForm] = useState({ code: '', name: '', type: 'GENERAL', parentCode: '', shortName: '' })

  const mutation = useMutation({
    mutationFn: accountingApi.createAccount,
    onSuccess: (account) => {
      qc.invalidateQueries({ queryKey: ['accounts'] })
      addNotification({
        type:    'success',
        title:   'Compte créé',
        message: account.requiresApproval
          ? `Compte ${account.code} créé — en attente d'approbation DAF.`
          : `Compte ${account.code} créé avec succès.`,
      })
      onClose()
      setForm({ code: '', name: '', type: 'GENERAL', parentCode: '', shortName: '' })
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message ?? 'Erreur création compte' })
    },
  })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(13,8,32,0.50)] backdrop-blur-sm">
      <div className="nx-card w-full max-w-2xl mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Nouveau compte</h2>
          <button onClick={onClose} className="text-[var(--tx3)] hover:text-[var(--tx)]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Code */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Code SYSCOHADA *</label>
            <input
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="ex: 4111, 601100"
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-[var(--tx3)] mt-1">
              Classe 1-8 (SYSCOHADA) ou 9 (analytique). La racine OHADA est vérifiée automatiquement.
            </p>
          </div>

          {/* Libellé */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Libellé *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ex: Clients locaux"
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Abrégé */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Abrégé</label>
            <input
              value={form.shortName}
              onChange={e => setForm(f => ({ ...f, shortName: e.target.value }))}
              placeholder="ex: Clients loc."
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1.5">Type</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
            >
              <option value="GENERAL">Général</option>
              <option value="AUXILIARY">Auxiliaire</option>
              <option value="ASSET">Actif</option>
              <option value="LIABILITY">Passif</option>
              <option value="EQUITY">Capitaux propres</option>
              <option value="REVENUE">Produit</option>
              <option value="EXPENSE">Charge</option>
            </select>
          </div>

          {/* Note DAF */}
          {form.code && !['1','2','3','4','5','6','7','8'].includes(form.code.charAt(0)) === false && form.code.length >= 4 && (
            <div className="flex items-start gap-2 px-3 py-2 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg text-xs text-[var(--amber-text)]">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Ce compte personnalisé sera soumis à l'approbation du DAF avant utilisation.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)] transition-colors">
            Annuler
          </button>
          <button
            onClick={() => mutation.mutate({ code: form.code, name: form.name, type: form.type, shortName: form.shortName || undefined })}
            disabled={!form.code || !form.name || mutation.isPending}
            className="px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {mutation.isPending ? 'Création…' : 'Créer le compte'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChartOfAccountsPage() {
  const { hasPermission } = useAuthStore()
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [search, setSearch]       = useState('')
  const [classFilter, setClass]   = useState<number | ''>('')
  const [showInactive, setShowI]  = useState(false)
  const [showModal, setModal]     = useState(false)
  const [expandedClass, setExpCls] = useState<number | null>(null)

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts', search, classFilter, showInactive],
    queryFn:  () => accountingApi.getAccounts({
      search:   search || undefined,
      class:    classFilter || undefined,
      isActive: showInactive ? undefined : true,
    }),
  })

  const approveMutation = useMutation({
    mutationFn: accountingApi.approveAccount,
    onSuccess: (acc) => {
      qc.invalidateQueries({ queryKey: ['accounts'] })
      addNotification({ type: 'success', title: 'Compte approuvé', message: `Compte ${acc.code} approuvé.` })
    },
  })

  // Grouper par classe
  const grouped = accounts.reduce<Record<number, Account[]>>((acc, a) => {
    const cls = a.class
    if (!acc[cls]) acc[cls] = []
    acc[cls].push(a)
    return acc
  }, {})

  const pendingApproval = accounts.filter(a => a.requiresApproval)

  return (
    <div className="space-y-5 nx-fade-up">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">Plan Comptable</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">SYSCOHADA 2018 · {accounts.length} comptes</p>
        </div>
        {hasPermission('accounting:create_account') && (
          <button
            onClick={() => setModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouveau compte
          </button>
        )}
      </div>

      {/* Alerte approbations en attente */}
      {pendingApproval.length > 0 && hasPermission('accounting:approve_account') && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-xl text-[var(--amber-text)] text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>
            <strong>{pendingApproval.length} compte(s)</strong> en attente d'approbation DAF :
            {pendingApproval.slice(0, 3).map(a => (
              <button
                key={a.id}
                onClick={() => approveMutation.mutate(a.id)}
                className="ml-2 underline hover:no-underline"
              >
                {a.code}
              </button>
            ))}
          </span>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un compte (code ou libellé)…"
            className="w-full pl-9 pr-4 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
          />
        </div>
        <select
          value={classFilter}
          onChange={e => setClass(e.target.value ? Number(e.target.value) : '')}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
        >
          <option value="">Toutes les classes</option>
          {[1,2,3,4,5,6,7,8,9].map(c => <option key={c} value={c}>Classe {c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-[var(--tx3)] cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowI(e.target.checked)}
            className="w-4 h-4 accent-blue-500"
          />
          Inactifs
        </label>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-12 bg-[var(--surface-2)] rounded-xl animate-pulse" />)}
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-10 text-[var(--tx3)]">
          <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p>Aucun compte trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([cls, accs]) => {
              const clsNum   = Number(cls)
              const isExpanded = expandedClass === clsNum || !!search || classFilter !== ''
              const color    = CLASS_COLORS[clsNum] ?? 'bg-[var(--bd2)]/10 text-[var(--tx3)]'

              return (
                <div key={cls} className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
                  {/* En-tête classe */}
                  <button
                    onClick={() => setExpCls(isExpanded && !search && classFilter === '' ? null : clsNum)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--surface-2)] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${color}`}>
                        Cl. {cls}
                      </span>
                      <span className="text-sm font-medium text-[var(--tx)]">{CLASS_LABELS[clsNum]}</span>
                      <span className="text-xs text-[var(--tx3)]">{accs.length} compte(s)</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-[var(--tx3)] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Lignes comptes */}
                  {isExpanded && (
                    <div className="border-t border-[var(--bd)] divide-y divide-[var(--bd)]">
                      {accs.map(account => (
                        <div
                          key={account.id}
                          className={`flex items-center justify-between px-4 py-2.5 text-sm ${!account.isActive ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {/* Indicateurs */}
                            {account.isOhadaLocked && (
                              <Lock className="w-3 h-3 text-[var(--tx3)] shrink-0" title="Compte normalisé SYSCOHADA" />
                            )}
                            {account.isSystemAccount && (
                              <ShieldAlert className="w-3 h-3 text-[var(--amber-text)] shrink-0" title="Compte système" />
                            )}
                            {account.requiresApproval && (
                              <span className="w-2 h-2 rounded-full bg-[var(--amber)] shrink-0" title="En attente d'approbation DAF" />
                            )}
                            {/* Code + libellé */}
                            <code className="text-[var(--blue-d)] font-mono text-xs w-16 shrink-0">{account.code}</code>
                            <span className="text-[var(--tx2)] truncate">{account.name}</span>
                            {account.shortName && (
                              <span className="text-[var(--tx3)] text-xs">({account.shortName})</span>
                            )}
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded ${account.normalBalance === 'DEBIT' ? 'bg-[var(--blue-bg)] text-[var(--blue-d)]' : 'bg-[var(--green)]/10 text-[var(--green-text)]'}`}>
                              {account.normalBalance}
                            </span>
                            {account.requiresApproval && hasPermission('accounting:approve_account') && (
                              <button
                                onClick={() => approveMutation.mutate(account.id)}
                                className="text-xs px-2 py-0.5 bg-[var(--amber-bg)] text-[var(--amber-text)] rounded hover:bg-[#FCD34D] transition-colors"
                              >
                                Approuver
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      )}

      <CreateAccountModal open={showModal} onClose={() => setModal(false)} />
    </div>
  )
}
